/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { isError } from "@epic-mw/error-types";
import useSWRImmutable from "swr/immutable";

import { useAccountId } from "../account";
import { useApiConfig } from "../api-config";
import { getApiPath } from "../utils";

export interface AOMPolicyRes {
	AOMFailed: boolean;
}

const getAOMPolicy = async (serverUrl?: string): Promise<AOMPolicyRes> => {
	try {
		const response = await fetch(`${getApiPath(serverUrl)}/policy/aom`);
		const json = await response.json();
		return {
			AOMFailed: json.failed,
		};
	} catch (err) {
		console.error("getAOMPolicy() failed to load", isError(err) && err.message);
		throw err;
	}
};

interface HookRes {
	result: AOMPolicyRes | undefined;
	isLoading: boolean;
	error: any;
}

export const useAccountAOMPolicy = (): HookRes => {
	const accountId = useAccountId();
	const { serverUrl } = useApiConfig();
	const shouldFetch = Boolean(accountId);

	const { data, error } = useSWRImmutable(
		shouldFetch ? [`aom-${accountId}`, serverUrl] : null,
		(_key, url) => getAOMPolicy(url),
		{ errorRetryCount: 0 },
	);

	return {
		result: data,
		isLoading: Boolean(!data && !error && shouldFetch),
		error,
	};
};
